import { generateClient, GraphQLResult } from "aws-amplify/api";
import { FieldContinuationDataForResolver, FieldTemplateForResolver } from "db/definitions";
import { bulkRetrieveFieldTemplatesByFormTemplate, createFieldTemplateForBatchResolver } from "db/queries/fieldTemplateQueries";
// @ts-ignore
import { getFieldTemplatesByFormTemplate } from "graphql/queries";
import { recordSentry } from "utils/recordSentry";

interface FieldTemplateWithContData extends FieldTemplateForResolver {
	continuationData?: FieldContinuationDataForResolver;
}

interface QueryResponse {
	fieldTemplatesByFormTemplate: {
		items: FieldTemplateWithContData[];
		nextToken?: string;
	};
}

/**
 * Given the formTemplate id and whether or not we're online, retrieves the fieldTemplates and returns them
 * @author Eric Webb and Robbie <ewebb@factorearth.com> <rmartin@factorearth.com>
 * @param id The formTemplate id we want to fetch the fieldTemplates by
 * @param online Whether or not we're online
 * @returns The array of fieldTemplates retrieved
 */
async function batchGetOnlyFieldTemplatesByFormTemplate(id: string, online: boolean): Promise<FieldTemplateForResolver[]> {
	const fieldTemplates: FieldTemplateForResolver[] = [];
	if (id) {
		try {
			if (online) {
				let token = undefined;
				while (token !== null) {
					const client = generateClient({ authMode: "userPool" });
					const result = (await client.graphql({
						query: getFieldTemplatesByFormTemplate,
						authMode: "userPool",
						variables: {
							formTemplate: id,
							nextToken: token,
						},
					})) as GraphQLResult<QueryResponse>;
					const fieldTemplatesFromResponse = result.data.fieldTemplatesByFormTemplate.items;
					for (const fieldTemplate of fieldTemplatesFromResponse) {
						const parsedFieldTemplate = createFieldTemplateForBatchResolver(fieldTemplate);
						fieldTemplates.push(parsedFieldTemplate);
					}
					token = result.data.fieldTemplatesByFormTemplate.nextToken || null;
				}
				return fieldTemplates;
			} else {
				const fieldTemplates = await bulkRetrieveFieldTemplatesByFormTemplate(id);
				return fieldTemplates;
			}
		} catch (err) {
			recordSentry({ error: err, msg: `ERROR GETTING FIELD TEMPLATE ${online ? "ONLINE" : "OFFLINE"}` });
			return fieldTemplates;
		}
	} else {
		return fieldTemplates;
	}
}

export default batchGetOnlyFieldTemplatesByFormTemplate;
